import { push } from 'connected-react-router';
import React, { useState, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from "react-redux";
import * as CONST from '../../../app/constants'
import { ERROR_STRING, VALIDATION_ERROR } from '../../../app/errors';
import DateSelectionForm from '../../../components/DateSelectionForm';
import {
    ErrorMark, 
    Header, 
    Button,
    NavigationLinkButton,
    MaxFileSizeUploadInstructionTextForSingleFile, 
} from '../../../components/RegularComponents';
import { currentAgentApplicationSelector } from '../../../redux/selector/collectorApplicationSelector';
import { applicationTypeFromLicenceSubType } from '../../../redux/slice/applicationsUtils';
import TopNavigationBar from '../../TopNavigationBar';
import {
    acceptedFileFormats,
    convertFileDataForUrl,
    extractBase64, 
    fileNameWithLengthLimit, 
    fileTypeEnum, 
    validateFileName
} from "../../../app/documentUploadUtils";
import { fetchDocumentData } from "../../../redux/slice/supportingDocumentsSlice";
import { DocumentViewerFileNameLink, DownloadFileNameLink } from "../../../components/DocumentViewer/DocumentFileComponent";
import DocumentViewer from "../../../components/DocumentViewer/DocumentViewer";

import { addCurrentAgentContinuingEducationCertificate, addCurrentAgentEducationCertificate, setCurrentAgentMortgageProperty } from "../../../redux/slice/additionalApplicationsSlice";
import { IsDateInFuture } from '../../../app/utils';
import { saveBusinessLicenceApplicationData } from '../../../redux/slice/businessLicenceApplicationSlice';

export const MraRenewalBrokerContinuingEducationCertificate = (props) => {
    const { 
        isModal, 
        onEditSave,
      } = props

    const dispatch = useDispatch();
    const agentApplication = useSelector(currentAgentApplicationSelector);
    const {regulatoryInfoDateState, mpcCompletionDateState} = useMemo(
      () => ({
          regulatoryInfoDateState: agentApplication?.businessLicenceData.mraBrokerRegulatoryInfoDate,
          mpcCompletionDateState : agentApplication?.businessLicenceData.mraBrokerMpcCompletionDate
      }), [agentApplication]
    );
    const educationContinuingFiles = useMemo(() => {
      const lastIssuedDate = agentApplication?.issuedDate
      return agentApplication?.documents?.filter(
                (document) => (document.documentType === CONST.DOCUMENT_TYPE_CONTINUING_EDUCATION_CERTIFICATE &&
                                document.changeType !== "DELETE" && 
                                (!document.createdDate || document.createdDate >= lastIssuedDate))
              )
    }, [agentApplication])

    const serverSaveRequest = useSelector(state => state.primaryAgencyApplication.serverRequestStatus)
    let applicationType = CONST.APPLICATION_TYPE_MORTGAGE_BROKERAGE_LICENCE
    if(agentApplication) { 
      const { licencingSubType } = agentApplication;
      applicationType = applicationTypeFromLicenceSubType(licencingSubType);
    }
    const isAMBApplication = (applicationType === CONST.APPLICATION_TYPE_MORTGAGE_ASSOCIATE_BROKER_LICENCE)
    const applicationMode = CONST.APPLICATION_MODE_RENEW
    const headerText = isAMBApplication ? "Associate Mortgage Broker Continuing Education" : "Mortgage Broker Continuing Education"

    const [regulatoryInfoDate, setRegulatoryInfoDate] = useState(regulatoryInfoDateState);
    const [mpcCompletionDate, setMpcCompletionDate] = useState(mpcCompletionDateState); 
    const [showValidationError, setShowValidationError] = useState(false);
    const [applicationSaveError, setApplicationSaveError] = useState(false);
    const [openDocumentViewer, setOpenDocumentViewer] = useState(false)
    const [documentViewerDocument, setDocumentViewerDocument] = useState(null)
    const [
        certificateMortgageEducationFile,
        setCertificateMortgageEducationFile,
    ] = useState(educationContinuingFiles?.[0]);

    const [regulatoryInfoDateError, setRegulatoryInfoDateError] = useState(
      VALIDATION_ERROR.NO_ERROR
    );

    const [mpcCompletionDateError, setMpcCompletionDateError] = useState(
      VALIDATION_ERROR.NO_ERROR
    );

    const [certificateFileError, setCertificateFileError] = useState(
      VALIDATION_ERROR.NO_ERROR
    );

    const fileSizeReplacement = (string, fileSize) => { 
      const replaceRegExp = /<file_size>/g 
      const errorString = string.replace(replaceRegExp,(fileSize + "MB"))
      return errorString
    }
    
    const handleFileInput = (e) => {
      if (e.target.files.length > 0) {
        let file = e.target.files[0];
        
        // individual file size can not be more than 15 MB
        if(file.size > (CONST.individualFileSizeAllowedInMB * (2 ** 20))) { 
          alert(fileSizeReplacement(ERROR_STRING.SUPPORTING_DOC_VERY_LARGE_INDIVIDUAL_FILE,CONST.individualFileSizeAllowedInMB))
          return
        } else if(!validateFileName(file.name)) {
          alert("File name is not valid. Please select another file.")
          return
        }

        let reader = new FileReader();
        reader.onload = (readerData) => {
          setCertificateMortgageEducationFile({
              filename: fileNameWithLengthLimit(file.name),
              documentContentType: fileTypeEnum(file.type),
              data: extractBase64(readerData.target.result),
              changeType: "SAVE",
              documentType: CONST.DOCUMENT_TYPE_CONTINUING_EDUCATION_CERTIFICATE,
              fileSize : file.size
          });
        };
        reader.readAsDataURL(file);

        // reset validation errors
        setCertificateFileError(VALIDATION_ERROR.NO_ERROR);
      }
    };

    const validateDate = (date) => {
      return date && !IsDateInFuture(date)
    }

    const validateData = () => {
      let ret = true;

      if(!isAMBApplication && !validateDate(regulatoryInfoDate)) {
        setRegulatoryInfoDateError(VALIDATION_ERROR.DATE_INVALID)
        ret = false;
      } else {
        setRegulatoryInfoDateError(VALIDATION_ERROR.NO_ERROR)
      }
      
      if(!validateDate(mpcCompletionDate)) {
        setMpcCompletionDateError(VALIDATION_ERROR.DATE_INVALID)
        ret = false;
      } else {
        setMpcCompletionDateError(VALIDATION_ERROR.NO_ERROR)
      }

      if(!certificateMortgageEducationFile || Object.keys(certificateMortgageEducationFile).length === 0) {
        setCertificateFileError(VALIDATION_ERROR.INCOMPLETE)
        ret = false;
      } else {
        setCertificateFileError(VALIDATION_ERROR.NO_ERROR)
      }

      return ret;
    }

    const updateApplicationData = () => {
      dispatch(setCurrentAgentMortgageProperty({
        key: 'mraBrokerRegulatoryInfoDate',
        value: regulatoryInfoDate,
      }));

      dispatch(setCurrentAgentMortgageProperty({
        key: 'mraBrokerMpcCompletionDate',
        value: mpcCompletionDate,
      }));

      if(certificateMortgageEducationFile) {
        dispatch(addCurrentAgentContinuingEducationCertificate(certificateMortgageEducationFile))
      }
    }
      
    const handleSaveNext = () => {

      //reset errors
      setShowValidationError(false);
      setApplicationSaveError(false);

      if(!validateData()){
          setShowValidationError(true);
          return;
      }

      updateApplicationData();

      dispatch(saveBusinessLicenceApplicationData(applicationType, applicationMode))
      .then(data => {
        dispatch(push(`${CONST.ROUTE_PATH.MRA_APP}${CONST.ROUTE_PATH.MRA_RENEWAL_PRINCIPAL_BROKER_ACT_REGULATION_CONFIRMATION}`));
      })
      .catch(err => {
        setApplicationSaveError(true)
      })
    };

    const handleSaveAndClose = () => {
        //reset errors
      setShowValidationError(false);
      setApplicationSaveError(false);

      if(!validateData()){
          setShowValidationError(true);
          return;
      }
      
      updateApplicationData();

      dispatch(saveBusinessLicenceApplicationData(applicationType, applicationMode))
      .then(data => {
        onEditSave();
      })
      .catch(err => {
        setApplicationSaveError(true)
      })
    }

    const fileComponent =  useCallback(() => {
        const file = certificateMortgageEducationFile
    
        return (
          <>
              <div className="supportingDocumentsFileContentWrapper">
                  <span className="fileName">
                      {file.documentContentType === CONST.DOCUMENT_CONTENT_TYPE_PDF ? 
                          <DocumentViewerFileNameLink
                              id={"education-certificate-file-" + file.filename}
                              key={"education-certificate-file-" + file.filename}
                              file={file}
                              onClick={() => { 
                                  if(file.data) { 
                                      let fileDataUrl = convertFileDataForUrl(file.data, file.documentContentType)
                                      setDocumentViewerDocument(fileDataUrl)
                                      setOpenDocumentViewer(true)
                                  } else { 
                                      // download data and then view it.
                                      if(file.guid) { 
                                          dispatch(fetchDocumentData(file.guid))
                                              .then(downloadFile => {
                                                  if(downloadFile) { 
                                                      let fileDataUrl = convertFileDataForUrl(downloadFile.data, downloadFile.documentContentType)
                                                      setDocumentViewerDocument(fileDataUrl)
                                                      setOpenDocumentViewer(true)
                                                  }
                                              })
                                              .catch((error) => {
                                                  console.error(error)
                                              })
                                      }
                                  }
                              }}
                          /> : 
                          <DownloadFileNameLink
                              id={"education-certificate-file-" + file.filename}
                              componentKey={"education-certificate-file-" + file.filename}
                              file={file} 
                          />
                      }
                  </span>
                  
                  <NavigationLinkButton
                      id={"education-certificate-file-remove-link"}
                      text={"- Remove this file"}
                      onClick={() => {
                        setCertificateMortgageEducationFile({})
                        dispatch(addCurrentAgentEducationCertificate({
                          ...certificateMortgageEducationFile,
                          changeType: "DELETE",
                        }))
    
                      }}
                  />
              </div>
          </>
      )
    }, [certificateMortgageEducationFile, dispatch])
    
    return (
      <div className={!isModal ? "container" : ""}>
        {!isModal && (
          <>
            <TopNavigationBar
              {...props}
              applicationType={applicationType}
              applicationMode={applicationMode}
            />
            <Header
              style={{ paddingBottom: "30px" }}
              id="broker-education-cert-header"
              header={headerText}
            />
          </>
        )}
        <h3 style={{ marginBottom: 40 }}>
          Please provide dates and certificates for <u>continuing education</u> information only. Do
          not provide initial license dates or a copy of your initial education certificate.
        </h3>

        {!isAMBApplication && (
          <div className="date-container">
            <h3>What is the date of completion for the Regulatory Information Program?</h3>
            <span>
              <b>Enter Day Month Year. For Example, 05,09,2006.</b>
            </span>
            {/* Date */}
            <div className="row">
              <div className="col-lg-8">
                <div
                  className={
                    "ns-fieldset" +
                    (showValidationError && regulatoryInfoDateError !== VALIDATION_ERROR.NO_ERROR
                      ? " has-error ns-fieldset-message-error"
                      : "")
                  }
                >
                  <fieldset className="ns-fieldset">
                    <DateSelectionForm
                      id={"mra-regulatory-info-date"}
                      date={regulatoryInfoDate}
                      disableFutureDate
                      showValidationError={showValidationError}
                      onDateChange={(date) => {
                        setRegulatoryInfoDate(date);
                        setRegulatoryInfoDateError(
                          validateDate(date)
                            ? VALIDATION_ERROR.NO_ERROR
                            : VALIDATION_ERROR.DATE_INVALID
                        );
                      }}
                    />
                  </fieldset>
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="date-container topMarginLarge">
          <h3>What was the date of completion for the MPC Continuing Education Program?</h3>
          <span>
            <b>Enter Day Month Year. For Example, 05,09,2006.</b>
          </span>
          {/* Date */}
          <div className="row">
            <div className="col-lg-8">
              <div
                className={
                  "ns-fieldset" +
                  (showValidationError && mpcCompletionDateError !== VALIDATION_ERROR.NO_ERROR
                    ? " has-error ns-fieldset-message-error"
                    : "")
                }
              >
                <fieldset className="ns-fieldset">
                  <DateSelectionForm
                    id={"mra-mpc-completion-date"}
                    date={mpcCompletionDate}
                    disableFutureDate
                    showValidationError={showValidationError}
                    onDateChange={(date) => {
                      setMpcCompletionDate(date);
                      setMpcCompletionDateError(
                        validateDate(date)
                          ? VALIDATION_ERROR.NO_ERROR
                          : VALIDATION_ERROR.DATE_INVALID
                      );
                    }}
                  />
                </fieldset>
              </div>
            </div>
          </div>
        </div>
        <div className="row bottomMarginMedium">
          <div className="col-xs-12 col-lg-8">
            {certificateMortgageEducationFile &&
              Object.keys(certificateMortgageEducationFile).length > 0 &&
              fileComponent()}
          </div>
        </div>

        <div className="row">
          <div className="col-xs-12 col-lg-8">
            <div
              className={
                "ns-fieldset" +
                (showValidationError && certificateFileError !== VALIDATION_ERROR.NO_ERROR
                  ? " has-error ns-fieldset-message-error"
                  : "")
              }
            >
              <fieldset className="ns-fieldset" id="continuing-education-certificate-form">
                <div className={"ns-fieldset"} style={{ paddingBottom: "20px" }}>
                  <label
                    className="ns-label"
                    id="upload-certificate-education-file-label"
                    htmlFor="upload-certificate-education-file-input"
                  >
                    <b>Upload the Continuing Education Certificate:</b>
                  </label>
                  <div className="ns-file-input">
                    <input
                      disabled={
                        certificateMortgageEducationFile &&
                        Object.keys(certificateMortgageEducationFile).length > 0
                      }
                      className="ns-input"
                      style={{ width: "15em", display: "inline" }}
                      type="file"
                      value=""
                      accept={acceptedFileFormats(
                        CONST.DOCUMENT_TYPE_CONTINUING_EDUCATION_CERTIFICATE
                      ).join(",")}
                      id="upload-certificate-education-file-input"
                      name="upload-certificate-education-file-input"
                      multiple={false}
                      onInput={handleFileInput}
                    />
                  </div>
                </div>
              </fieldset>
            </div>
          </div>
        </div>

        <MaxFileSizeUploadInstructionTextForSingleFile />

        <div className="row">
          <div
            className={
              "col-lg-4" + (applicationSaveError || showValidationError ? " has-error" : "")
            }
          >
            <Button
              id="complete-mortgage-education-continue-button"
              style={{ margin: "0 10px 10px 0" }}
              buttonType={"regular_primary"}
              text={isModal ? "Save and close" : "Save and next"}
              onClick={isModal ? handleSaveAndClose : handleSaveNext}
              showSpinner={
                !applicationSaveError && serverSaveRequest === CONST.REQUEST_STATUS.IN_PROGRESS
              }
              disabled={serverSaveRequest === CONST.REQUEST_STATUS.IN_PROGRESS}
            />
            {(applicationSaveError || showValidationError) && ErrorMark()}
            {showValidationError && (
              <div className="ns-error-message">
                <span className="ns-visually-hidden">Error:</span>
                {ERROR_STRING.CHECK_FIELDS}
              </div>
            )}
            {applicationSaveError && (
              <div className="ns-error-message">
                <span className="ns-visually-hidden">Error:</span>
                {ERROR_STRING.APPLICATION_SAVE_ERROR}
              </div>
            )}
          </div>
        </div>

        <DocumentViewer
          fileData={documentViewerDocument}
          isOpen={openDocumentViewer}
          fileName={certificateMortgageEducationFile?.filename}
          handleClose={() => setOpenDocumentViewer(false)}
        />
      </div>
    );
}